import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Requisition {
  id: string
  title: string
  department: string
  requester: string
  status: string
  priority: string
  total_amount: number
  created_date: string
}

interface Vendor {
  id: string
  name: string
  category: string
  rating: number
  status: string
  total_orders: number
  total_spent: number
  contact_email: string
}

export default function ProcurementPage() {
  const [requisitions, setRequisitions] = useState<Requisition[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [reqRes, vendorRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/procurement/requisitions'),
      fetch(API_BASE + '/procurement/vendors'),
      fetch(API_BASE + '/procurement/statistics')
    ])
    if (reqRes.ok) setRequisitions((await reqRes.json()).requisitions || [])
    if (vendorRes.ok) setVendors((await vendorRes.json()).vendors || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': case 'active': case 'completed': return 'bg-green-100 text-green-800'
      case 'pending': case 'submitted': return 'bg-yellow-100 text-yellow-800'
      case 'in_review': case 'processing': return 'bg-blue-100 text-blue-800'
      case 'rejected': case 'inactive': return 'bg-red-100 text-red-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      default: return 'text-gray-600'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
  }

  const renderStars = (rating: number) => {
    return '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating))
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Procurement</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Requisition</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Add Vendor</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Requisitions</div>
              <div className="text-2xl font-bold">{statistics.open_requisitions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Vendors</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.active_vendors}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Approval</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_approval}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Spend MTD</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_spend_mtd)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Vendor Rating</div>
              <div className="text-2xl font-bold">{statistics.avg_vendor_rating?.toFixed(1)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Requisitions</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {requisitions.slice(0, 10).map(req => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{req.title}</div>
                      <div className="text-xs text-gray-500">{req.department} • {req.requester}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold">{formatCurrency(req.total_amount)}</div>
                      <span className={`text-xs ${getPriorityColor(req.priority)}`}>{req.priority}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(req.status)}`}>
                        {req.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Vendors</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {vendors.slice(0, 10).map(vendor => (
                  <tr key={vendor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{vendor.name}</div>
                      <div className="text-xs text-gray-500">{vendor.category}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-yellow-500 text-sm">{renderStars(vendor.rating)}</div>
                      <div className="text-xs text-gray-500">{vendor.total_orders} orders • {formatCurrency(vendor.total_spent)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(vendor.status)}`}>
                        {vendor.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
